import { personPropsChanged } from '@flashka/shared/analytics/person-props-once';

import posthog from 'posthog-js';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';

import { useAuth, useUser } from '@/auth/better/session';
import { personPropsStore } from '@/lib/analytics/person-props-store';
import { useShareAttributionCapture } from '@/lib/social/analytics';
import { isLandingShellPath } from '@/shell-paths';

/**
 * Which landing the visitor saw, stamped on every event they send from then on
 * (a persisted super property, so it survives to the sign-up days later). The
 * old landing never set it: `landing_version` empty = old landing, `v3` = the
 * redesign — one PostHog breakdown compares them. Bump on the next redesign.
 */
const LANDING_VERSION = 'v3';

export function tagLandingVersion(client: typeof posthog = posthog): void {
  client.register({ landing_version: LANDING_VERSION });
}

posthog.init(import.meta.env.VITE_POSTHOG_KEY as string, {
  api_host: import.meta.env.VITE_POSTHOG_HOST,
  person_profiles: 'identified_only',
  autocapture: false,
  /**
   * Let the E2E suite see the events this app fires.
   *
   * `posthog-js` gates every capture on its own bot check, whose blocked-UA
   * list includes `headlesschrome` — so under Playwright it silently discarded
   * 100% of captures, and the suite's analytics channel could only ever record
   * an empty, convincing zero. That makes a tracking regression undetectable:
   * "no events fired" looks identical whether the product stopped reporting or
   * the library refused to send.
   *
   * Scoped to a flag only an E2E run sets (`e2e/support/gate/analytics.ts` installs
   * it via an init script, which runs before this module is imported). Real
   * browsers never set it, so bot filtering is unchanged in production — and
   * deliberately NOT keyed on `navigator.webdriver`, which would un-filter any
   * automated browser pointed at prod and change which traffic lands in the
   * real project.
   */
  opt_out_useragent_filter:
    typeof window !== 'undefined' &&
    (window as unknown as { __FLASHKA_E2E__?: boolean }).__FLASHKA_E2E__ === true,
  // `$pageleave` was a fifth of web's volume and nothing reads it.
  capture_pageleave: false,
  // Runs before the initial `$pageview`, so a visit that opens on the landing
  // has its very first pageview tagged too.
  loaded: (client) => {
    if (isLandingShellPath(window.location.pathname)) tagLandingVersion(client);
  },
});
console.log('PHProvider');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function PHProvider({ children }: { children: any }) {
  const { userId } = useAuth();
  const { user } = useUser();
  const posthog = usePostHog();
  // A primitive, so the identify effect below runs at most twice per login
  // (before / after the user record lands) instead of on every session
  // re-render.
  const createdAtISO =
    user && user.id === userId && user.createdAt ? user.createdAt.toISOString() : null;

  // Share-link attribution (`?ref` / `?ch`). Mounted HERE, above every route
  // guard, because `/profile/:locator` is behind `ProtectedRoute` — a
  // signed-out share-link recipient would otherwise lose the attribution at
  // the sign-in wall, which is exactly the conversion the params exist to
  // measure. See `useShareAttributionCapture`.
  useShareAttributionCapture();

  useEffect(() => {
    if (!userId) return;
    // Mirror mobile (`flashka-app/src/components/posthog.tsx`): stamp the
    // account's creation time so signup-week cohorts (consent funnel,
    // retention-by-mates) include web users. `account_created_at` is the
    // canonical key (Better Auth); `clerk_created_at` is the same value under
    // its legacy name, kept only for cohorts built before the auth migration.
    // `app_surface` is deliberately NOT set from web: mobile stamps `'mobile'`
    // for its own flag targeting, and a cross-platform user would otherwise
    // flip it on every web login.
    const props = createdAtISO
      ? { account_created_at: createdAtISO, clerk_created_at: createdAtISO }
      : undefined;
    // A first identify (another id still in the SDK) always goes — it is the
    // `$identify` that merges the anonymous person. After that a repeat with
    // properties is a billed `$set`, so only a CHANGED batch is sent, and
    // "already sent" survives reloads (`personPropsChanged`).
    const changed = props
      ? personPropsChanged(personPropsStore, 'account', userId, props)
      : false;
    if (!changed && posthog.get_distinct_id() === userId) return;
    posthog.identify(userId, props);
  }, [posthog, userId, createdAtISO]);

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}
